// External Dependencies
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TableViewIcon from '@mui/icons-material/TableView';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsMap from 'highcharts/modules/map';

// Internal Dependencies
import {
  EnhancedButton,
  EnhancedCard,
  EnhancedCardContent,
  Subtitle,
} from 'components/shared';

// Local Dependencies
import HeatMapDrawer from './Drawer';
import { FipsData, useChartState } from './hooks';

// Initialize Highcharts map module
if (typeof window !== 'undefined') {
  highchartsMap(Highcharts);
}

// Component Definition
const OrganizationsHeatMap = (): JSX.Element => {
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedFips, setSelectedFips] = useState<FipsData | null>(null);
  const [expandedDistricts, setExpandedDistricts] = useState<Set<string>>(new Set());

  const navigate = useNavigate();

  const handleClickOrganizationsTable = () => {
    navigate('/organizations');
  };

  // Handle closing the drawer
  const handleCloseDrawer = () => {
    setSelectedFips(null);
    // Reset expanded districts when closing the drawer
    setExpandedDistricts(new Set());
  };

  // Handle toggling admin portal expansion
  const handleToggleAdminPortal = (adminPortalLabel: string) => {
    setExpandedDistricts((prevExpanded) => {
      const newExpanded = new Set(prevExpanded);
      if (newExpanded.has(adminPortalLabel)) {
        newExpanded.delete(adminPortalLabel);
      } else {
        newExpanded.add(adminPortalLabel);
      }
      return newExpanded;
    });
  };

  const {
    chartData,
    chartOptions,
    isLoading,
    isLoadingStateMap,
    organizationsForSelectedFips,
  } = useChartState({
    selectedFips,
    selectedState,
    setSelectedFips,
    setSelectedState,
  });

  return (
    <>
      <Box
        alignItems="flex-start"
        display="flex"
        justifyContent="space-between"
      >
        <Subtitle>
          Organization Heat Map
        </Subtitle>

        <EnhancedButton
          onClick={handleClickOrganizationsTable}
          size="small"
          startIcon={<TableViewIcon fontSize="small" />}
          variant="outlined"
        >
          Table
        </EnhancedButton>
      </Box>

      {isLoading || isLoadingStateMap ? (
        <div>Loading...</div>
      ) : (
        <Box
          height={500}
          width="100%"
        >
          <EnhancedCard>
            <EnhancedCardContent>
              {selectedState && (
                <EnhancedButton
                  onClick={() => setSelectedState(null)}
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  sx={{ ml: 2 }}
                  variant="outlined"
                >
                  Back to US Map
                </EnhancedButton>
              )}

              {chartData && chartData.length > 0 ? (
                <HighchartsReact
                  constructorType="mapChart"
                  highcharts={Highcharts}
                  options={chartOptions}
                />
              ) : (
                <p>No organization data available for the heat map</p>
              )}
            </EnhancedCardContent>
          </EnhancedCard>
        </Box>
      )}

      {/* Drawer for displaying organizations in selected FIPS */}
      <HeatMapDrawer
        expandedDistricts={expandedDistricts}
        handleCloseDrawer={handleCloseDrawer}
        handleToggleDistrict={handleToggleAdminPortal}
        organizations={organizationsForSelectedFips}
        selectedFips={selectedFips}
      />
    </>
  );
};

export default OrganizationsHeatMap;
